import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Grid, GridColumn, GridPageChangeEvent } from "@progress/kendo-react-grid";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { apiClient } from "src/dm/utils/axios";

interface AuditAllowance {
  auditAllowanceId: number;
  dealId: number | null;
  bannerCode: string | null;
  categoryManager: string | null;
  apVendorName: string | null;
  apVendorNumber: string | null;
  vendorBrokerId: number | null;
  dealTypeDescription: string | null;
  description: string | null;
}

interface AuditResponse {
  items: AuditAllowance[];
  total: number;
  page: number;
  pageSize: number;
}

const AuditReport = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [bannerCode, setBannerCode] = useState("");
  const [categoryManager, setCategoryManager] = useState("");

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["audit-allowance", page, pageSize, bannerCode, categoryManager],
    queryFn: async (): Promise<AuditResponse> => {
      const response = await apiClient.get<AuditResponse>(
        "/dm/V1/audit/allowances",
        {
          params: {
            page,
            pageSize,
            bannerCode: bannerCode || undefined,
            categoryManager: categoryManager || undefined,
          },
        }
      );
      return response.data;
    },
  });

  const handlePageChange = (e: GridPageChangeEvent) => {
    setPage(Math.floor(e.page.skip / e.page.take) + 1);
    setPageSize(e.page.take);
  };

  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <h1 style={{ marginTop: 0 }}>Audit Report — Allowances</h1>

      <section
        style={{
          background: "#fff",
          padding: "1rem 1.25rem",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          display: "grid",
          gridTemplateColumns: "1fr 1fr auto",
          gap: "0.75rem",
          alignItems: "end",
        }}
      >
        <label>
          <span style={{ fontSize: "0.8rem", color: "#475569" }}>Banner</span>
          <Input
            value={bannerCode}
            onChange={(e) => setBannerCode(String(e.target.value ?? ""))}
            placeholder="GE / MA / …"
          />
        </label>
        <label>
          <span style={{ fontSize: "0.8rem", color: "#475569" }}>Category Manager</span>
          <Input
            value={categoryManager}
            onChange={(e) => setCategoryManager(String(e.target.value ?? ""))}
          />
        </label>
        <Button
          themeColor="primary"
          onClick={() => setPage(1)}
        >
          Refresh
        </Button>
      </section>

      <section
        style={{
          background: "#fff",
          padding: "1rem 1.25rem",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        {isLoading && <p>Loading audit records…</p>}
        {isError && (
          <p style={{ color: "#b91c1c" }}>
            {error instanceof Error ? error.message : "Failed to load"}
          </p>
        )}
        {data && (
          <>
            <p style={{ color: "#555", margin: 0, marginBottom: "0.5rem" }}>
              {data.total.toLocaleString()} audit record{data.total === 1 ? "" : "s"}
            </p>
            <Grid
              data={data.items}
              skip={(page - 1) * pageSize}
              take={pageSize}
              total={data.total}
              pageable={{ pageSizes: [25, 50, 100, 200] }}
              onPageChange={handlePageChange}
            >
              <GridColumn field="auditAllowanceId" title="Audit ID" width="100px" />
              <GridColumn field="dealId" title="Deal" width="100px" />
              <GridColumn field="bannerCode" title="Banner" width="100px" />
              <GridColumn field="categoryManager" title="Cat Mgr" width="160px" />
              <GridColumn field="dealTypeDescription" title="Deal Type" width="140px" />
              <GridColumn field="apVendorNumber" title="AP Vendor #" width="120px" />
              <GridColumn field="apVendorName" title="AP Vendor Name" />
              <GridColumn field="description" title="Description" />
            </Grid>
          </>
        )}
      </section>
    </div>
  );
};

export default AuditReport;
